import { TrashIcon } from '@heroicons/react/16/solid';
import uniqueId from 'lodash.uniqueid';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

const AddSKUModal = ({
	isOpen = false,
	currentSKUs = [],
	availableSKUs = [],
	onCancel,
	onAddSKUs,
}) => {
	const modalRef = useRef();
	const [chosenSKU, setChosenSKU] = useState(0);
	const [newSKUs, setNewSKUs] = useState([]);
	const [showAddAnother, setShowAddAnother] = useState(false);
	useEffect(() => {
		if (isOpen) {
			modalRef.current.showModal();
			setNewSKUs([]);
		} else {
			setChosenSKU(0);
			setNewSKUs([]);
			setShowAddAnother(false);
		}
	}, [isOpen]);
	useEffect(() => {
		if (chosenSKU !== 0) {
			const selectedSKU = availableSKUs.find(
				(sku) => sku.id === parseInt(chosenSKU, 10),
			);
			setNewSKUs([
				...newSKUs,
				{ id: uniqueId('new-sku-'), taxonomy_sku: selectedSKU },
			]);
			setChosenSKU(0);
			setShowAddAnother(false);
		}
	}, [chosenSKU]);

	const handleAddSKUs = () => {
		onAddSKUs(newSKUs);
	};

	const filteredAvailableSKUs = availableSKUs.filter(
		(sku) =>
			!currentSKUs.find(
				(currentSKU) => currentSKU.taxonomy_sku?.id === sku.id,
			) && !newSKUs.find((newSKU) => newSKU.taxonomy_sku.id === sku.id),
	);

	return (
		<dialog id="add-sku-dialog" className="modal" ref={modalRef}>
			<div className="modal-box">
				<div className="flex items-center justify-between mb-4">
					<h3>Add SKUs</h3>
					<button
						className="btn btn-sm btn-primary"
						onClick={() => setShowAddAnother(true)}
						disabled={showAddAnother || filteredAvailableSKUs.length === 0}
					>
						Add
					</button>
				</div>
				<table className="table w-full mb-4">
					<thead>
						<tr>
							<th>SKU Number</th>
							<th>Label</th>
						</tr>
					</thead>
					<tbody>
						{currentSKUs?.map(({ id, taxonomy_sku }) =>
							taxonomy_sku ? (
								<tr key={`current-sku-${id}`} className="opacity-60">
									<td>{taxonomy_sku.sku_number}</td>
									<td>{taxonomy_sku.label}</td>
								</tr>
							) : null,
						)}
						{newSKUs?.map(({ id, taxonomy_sku }) => (
							<tr key={`new-sku-${id}`}>
								<td>{taxonomy_sku.sku_number}</td>
								<td className="flex items-center justify-between">
									<div>{taxonomy_sku.label}</div>
									<button
										type="button"
										className="btn btn-xs btn-circle btn-ghost"
										onClick={() =>
											setNewSKUs(newSKUs.filter((sku) => sku.id !== id))
										}
									>
										<TrashIcon className="w-4 h-4" />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
				{showAddAnother && (
					<div className="py-4 form-control">
						<label htmlFor="add-sku" className="label">
							SKU
						</label>
						<select
							id="add-sku"
							className="select select-bordered select-sm"
							value={chosenSKU}
							onChange={({ target }) => setChosenSKU(target.value)}
							autoComplete="off"
							required
						>
							<option disabled value="0">
								Choose...
							</option>
							{filteredAvailableSKUs?.map((sku) => (
								<option key={`${sku.id}`} value={sku.id}>
									{sku.sku_number} - {sku.label}
								</option>
							))}
						</select>
					</div>
				)}
				<div className="modal-action">
					<form method="dialog">
						<button className="btn" onClick={onCancel}>
							Cancel
						</button>
						<button
							className="ml-4 btn btn-primary"
							onClick={handleAddSKUs}
							disabled={newSKUs.length === 0}
						>
							Save
						</button>
					</form>
				</div>
			</div>
		</dialog>
	);
};

AddSKUModal.propTypes = {
	isOpen: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	currentSKUs: PropTypes.array,
	availableSKUs: PropTypes.array,
	onAddSKUs: PropTypes.func.isRequired,
};

export default AddSKUModal;
